import { useTranslation } from "react-i18next";

import type { DiagnosticSnapshot, SectionSource } from "@/api/diagnostic";
import { formatDate } from "@/lib/datetime";
import { cn } from "@/lib/utils";

type Snapshots = DiagnosticSnapshot["snapshots"];

interface SourceFooterProps {
  source: SectionSource;
  /** Resolve run e data. O envelope só carrega o **nome** do snapshot. */
  snapshots?: Snapshots;
  className?: string;
}

interface Provenance {
  run: string | null;
  when: string | null;
}

function provenance(source: SectionSource, snapshots?: Snapshots): Provenance {
  if (!snapshots) return { run: null, when: null };
  if (source.snapshot === "functional") {
    return { run: snapshots.functional?.execution_id ?? null, when: snapshots.functional?.completed_at ?? null };
  }
  if (source.snapshot === "architect") {
    return { run: snapshots.architect?.execution_id ?? null, when: snapshots.architect?.completed_at ?? null };
  }
  // `project` (tabela) e `derived` não têm run próprio — a procedência é o `ref`.
  return { run: null, when: null };
}

/**
 * O último segmento do caminho do payload.
 *
 * O `ref` do envelope é o endereço técnico completo
 * (`output_summary.extraction_summary.license_measurement`), e ele existe para
 * quem precisa rastrear. Na linha que o cliente lê, o que importa é o nome da
 * fonte. O caminho inteiro continua no `title`.
 *
 * `ref` sem ponto nenhum (tabela de projeto, `derived`) volta inteiro: não há o
 * que cortar.
 */
function shortRef(ref: string): string {
  const parts = ref.split(".");
  return parts[parts.length - 1] || ref;
}

/**
 * Os oito primeiros caracteres do id da execução.
 *
 * Um UUID inteiro na linha de origem ocupa metade dela e não é lido por ninguém
 * na sala. Oito caracteres bastam para casar com o log; o id completo fica no
 * `title`, que é onde quem vai copiar procura.
 */
const RUN_PREFIX_LENGTH = 8;

/** Rodapé de procedência: todo card do Diagnóstico mostra de onde o número veio. */
export function SourceFooter({ source, snapshots, className }: SourceFooterProps) {
  const { t } = useTranslation();
  const { run, when } = provenance(source, snapshots);

  return (
    <div
      data-testid="diagnostic-source-footer"
      className={cn(
        "mt-3 border-t border-[#f2f4f7] pt-2 font-mono text-[10px] tracking-[0.04em] text-[#7b8799]",
        className,
      )}
    >
      <span>{t("Origin")}: </span>
      <span>{source.snapshot}</span>
      <span> · </span>
      <span title={source.ref}>{shortRef(source.ref)}</span>
      {run && (
        <>
          <span> · </span>
          <span title={run}>
            {t("run")} {run.slice(0, RUN_PREFIX_LENGTH)}
          </span>
        </>
      )}
      {when && (
        <>
          <span> · </span>
          <span>{formatDate(when)}</span>
        </>
      )}
    </div>
  );
}
